import { Fragment, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import FieldRow from './FieldRow';
import { DividerStyled, SectionCard, SectionCardContent, SectionTitleWrapper } from '../styled';
import {
  ExpandMoreIconStyled,
  MoreDetailsAccordion,
  MoreDetailsAction,
  MoreDetailsBody,
  MoreDetailsSummary,
} from './styled';
import { CardSectionVM } from '../types';
import usePinnedFields from './usePinnedFields';

const Section = ({ actions, fields, icon, moreDetails, pinScope, title }: CardSectionVM & { pinScope: string }) => {
  if (!fields?.length) return null;

  const [expanded, setExpanded] = useState(moreDetails?.defaultExpanded ?? false);

  const { isPinned, togglePin } = usePinnedFields(pinScope);

  const coreVisible = fields;
  const more = moreDetails?.fields ?? [];

  // Only fields that are NOT pinnedByDefault can be pinned/unpinned
  const pinnableMore = more?.filter((f) => !f.pinnedByDefault);
  const nonPinnableMore = more?.filter((f) => !!f.pinnedByDefault);

  // Promote pinned pinnable fields
  const promoted = pinnableMore?.filter((f) => isPinned(f.key));
  const remainingMore = [
    ...nonPinnableMore, // always stay in accordion
    ...pinnableMore?.filter((f) => !isPinned(f.key)),
  ];

  return (
    <SectionCard variant="outlined">
      <SectionCardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <SectionTitleWrapper>
            {icon ? <FontAwesomeIcon size="lg" icon={icon as IconProp} /> : null}
            <Typography variant="h6">{title}</Typography>
          </SectionTitleWrapper>
          {actions}
        </Stack>

        <DividerStyled />

        {/* Static Fields */}

        {coreVisible?.map((f) => (
          <FieldRow key={f.key} field={f} isPinned={isPinned} showPinControl={false} togglePin={togglePin} />
        ))}

        {promoted?.map((f) => (
          <FieldRow key={f.key} field={f} isPinned={isPinned} showPinControl={true} togglePin={togglePin} />
        ))}

        {/* More Details */}

        {remainingMore?.length || moreDetails?.customFields?.length ? (
          <MoreDetailsAccordion
            disableGutters
            elevation={0}
            expanded={expanded}
            onChange={(_, isExpanded) => setExpanded(isExpanded)}
          >
            <MoreDetailsSummary expandIcon={<ExpandMoreIconStyled />}>
              <MoreDetailsAction>{expanded ? 'show less' : (moreDetails?.title ?? 'show more')}</MoreDetailsAction>
            </MoreDetailsSummary>

            <MoreDetailsBody>
              {remainingMore?.map((f) => (
                <FieldRow key={f.key} field={f} isPinned={isPinned} showPinControl={true} togglePin={togglePin} />
              ))}

              {moreDetails?.customFields?.map((customField, index) => (
                <Fragment key={`custom-field-${index}`}>{customField}</Fragment>
              ))}
            </MoreDetailsBody>
          </MoreDetailsAccordion>
        ) : null}
      </SectionCardContent>
    </SectionCard>
  );
};

export default Section;
